import React from "react";
import { Lock, CheckCircle2 } from "lucide-react";
import { lastName } from "./utils";
import { getTeamVocab, cap } from "../../../constants/uiRegistry";

// FR shows a decimal route load against an 11-hour capacity band ("7.33/11 hrs").
const ROUTE_CAPACITY_HOURS = 11;

// "Tue 10/06" — the FR route-header date.
const formatServiceDay = (value) => {
  const source = String(value || "").trim();
  if (!source) return "";
  const parsed = new Date(`${source}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return source;
  const wd = parsed.toLocaleDateString(undefined, { weekday: "short" });
  return `${wd} ${String(parsed.getMonth() + 1).padStart(2, "0")}/${String(parsed.getDate()).padStart(2, "0")}`;
};

// The route header in FieldRoutes' shape — three lines, ~64px:
//   TECH NAME                     Appts 17 · 7.33/11 hrs
//   Tue 10/06 · Regular Routes · ID 54360
//   Office Staff · Technicians · Sales Reps · API
// No badges, no meter, no chips. Lock / confirmed marks sit in the right corner
// only when the route carries them.
function TechnicianHeader({ route }) {
  const totalStops = Number(route?.metrics?.totalStops || 0);
  // Booked = service minutes (not routeMinutes, which folds in travel); available
  // = the route's real capacity when known, else the standard 11 h band.
  const bookedHours = Number(route?.metrics?.scheduledMinutes || 0) / 60;
  const capacityMinutes = Number(route?.metrics?.capacityMinutes || 0);
  const availableHours = capacityMinutes > 0 ? capacityMinutes / 60 : ROUTE_CAPACITY_HOURS;
  const over = availableHours > 0 && bookedHours > availableHours;
  const bookedLabel = bookedHours ? Number(bookedHours.toFixed(2)).toString() : "0";
  const availableLabel = Number(availableHours.toFixed(availableHours % 1 ? 1 : 0)).toString();
  const technicianName = route?.technicianName || "Unassigned";
  const isPool = !String(route?.technicianId || "").trim();
  const displayName = isPool ? technicianName : lastName(technicianName).toUpperCase();
  const routeType = route?.routeTitle || "Regular Routes";
  const routeId = route?.routeId || "";
  const serviceDay = formatServiceDay(route?.serviceDay);
  // lock_state is "locked" (FR lockedRoute, or a finalized stop on it) or "finalized".
  const locked = ["locked", "finalized"].includes(String(route?.lockState || "").toLowerCase());
  const confirmed = /^confirmed$/i.test(String(route?.status || ""));

  return (
    <header className="dispatch-route-header dispatch-route-header--fr">
      <div className="dispatch-route-title-row">
        <h3 className="dispatch-route-name" title={technicianName}>{displayName}</h3>
        <span className="dispatch-route-header-counts">
          <span className="dispatch-route-appt-count">Appts {totalStops}</span>
          <span className="dispatch-route-sep">·</span>
          <span className={`dispatch-route-hours${over ? " is-over" : ""}`} title={over ? "Over capacity" : "Booked / available hours"}>
            {bookedLabel}/{availableLabel} hrs
          </span>
          {locked ? <Lock size={11} className="dispatch-route-mark" aria-label="Route locked" /> : null}
          {confirmed && !locked ? <CheckCircle2 size={11} className="dispatch-route-mark" aria-label="Route confirmed" /> : null}
        </span>
      </div>
      <div className="dispatch-route-subline">
        {serviceDay ? <span>{serviceDay}</span> : null}
        <span>{routeType}</span>
        {routeId ? <span>ID {routeId}</span> : null}
      </div>
      {/* Who-can-schedule (FR parity). Static labels until route policy is modelled. */}
      {!isPool ? (
        <div className="dispatch-route-audience">
          <span>Office Staff</span><span>{cap(getTeamVocab().members)}</span><span>Sales Reps</span><span>API</span>
        </div>
      ) : <div className="dispatch-route-audience"><span>Drop a {String(route?.stopWord || "job")} here to unassign it</span></div>}
    </header>
  );
}

export default React.memo(TechnicianHeader);
